import { useState, useMemo } from "react";
import { useAuth } from "@/App";

/**
 * useRegionFilter — single source of truth for region scoping across admin pages.
 *
 * - Super Admin sees the RegionSwitcher (ALL/GJ/RJ) and choice persists in localStorage.
 * - Non-super-admins are pinned to their own user.region_id and the switcher is hidden.
 *
 * Usage:
 *   const { activeRegion, setActiveRegion, isSuperAdmin, filterByRegion } = useRegionFilter();
 *   const visible = filterByRegion(items, (item) => item.region_id);
 */
export function useRegionFilter() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const [activeRegion, _setActiveRegion] = useState(() =>
    isSuperAdmin
      ? (localStorage.getItem("pravaah_region") || "ALL")
      : (user?.region_id || "ALL")
  );

  const setActiveRegion = (r) => {
    _setActiveRegion(r);
    if (isSuperAdmin) localStorage.setItem("pravaah_region", r);
  };

  const filterByRegion = useMemo(() => {
    return (items, getRegion) => {
      if (!Array.isArray(items)) return [];
      if (!activeRegion || activeRegion === "ALL") return items;
      return items.filter((it) => {
        const r = typeof getRegion === "function" ? getRegion(it) : (it?.region_id);
        return r === activeRegion;
      });
    };
  }, [activeRegion]);

  return { activeRegion, setActiveRegion, isSuperAdmin, filterByRegion };
}

export default useRegionFilter;
